import dynamic from 'next/dynamic';
import React from 'react';
import SemData from '~/components/sem/SemData';
import {
  browserCookieIdExperiments,
  userIdExperiments
} from '~/constants/sem/experiments';
import AnalyticsProvider from '~/context/common/AnalyticsProvider';
import {
  DeviceContextProvider,
  type OwnProps as DeviceContextProps
} from '~/context/common/DeviceContext';
import ExperimentProvider, {
  type PropsFromServer as ExperimentContextProps
} from '~/context/common/ExperimentProvider';
import {
  type OwnProps as StaticContextProps,
  StaticContextProvider
} from '~/context/common/StaticContext';
import {
  type OwnProps as UserContextProps,
  UserContextProvider
} from '~/context/common/UserContext';
import { ModalContextProvider } from '~/context/sem/ModalContext';
import {
  type OwnProps as SemStaticContextProps,
  StaticContextProvider as SemStaticContextProvider
} from '~/context/sem/StaticContext';

const CoreAuth = dynamic(
  () => import(/* webpackChunkName: "CoreAuth" */ '@/common/CoreAuth'),
  { ssr: false }
);

export type SemProvidersProps = {
  forDeviceContext: DeviceContextProps;
  forExperimentContext: ExperimentContextProps;
  forSemStaticContext: SemStaticContextProps;
  forStaticContext: StaticContextProps;
  forUserContext: UserContextProps;
};

type OwnProps = React.PropsWithChildren<
  SemProvidersProps & {
    eventableProject: string;
    eventableTrack: string;
  }
>;

const SemProviders = ({
  children,
  eventableProject,
  eventableTrack,
  forDeviceContext,
  forExperimentContext,
  forSemStaticContext,
  forStaticContext,
  forUserContext
}: OwnProps) => (
  <StaticContextProvider {...forStaticContext}>
    <SemStaticContextProvider {...forSemStaticContext}>
      <ExperimentProvider
        {...forExperimentContext}
        browserCookieIdExperiments={browserCookieIdExperiments}
        userIdExperiments={userIdExperiments}
      >
        <AnalyticsProvider
          projectName={eventableProject}
          track={eventableTrack}
        >
          <DeviceContextProvider {...forDeviceContext}>
            <UserContextProvider {...forUserContext}>
              <ModalContextProvider>
                <CoreAuth />
                <SemData />
                {children}
              </ModalContextProvider>
            </UserContextProvider>
          </DeviceContextProvider>
        </AnalyticsProvider>
      </ExperimentProvider>
    </SemStaticContextProvider>
  </StaticContextProvider>
);

export default SemProviders;
